// ── Global Jurisdiction Data ──────────────────────────────────────────────
// Each entry defines: regions (states/provinces), legal system type,
// witness requirements, notarisation, executor terminology, and key nuances.

export const COUNTRIES = [
  // ── Americas ──────────────────────────────────────────────────────────
  {
    value: 'US', label: '🇺🇸 United States', legalSystem: 'Common Law',
    executorTerm: 'Executor / Personal Representative',
    regions: [
      'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
      'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
      'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
      'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada',
      'New Hampshire','New Jersey','New Mexico','New York','North Carolina',
      'North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island',
      'South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont',
      'Virginia','Washington','West Virginia','Wisconsin','Wyoming',
      'District of Columbia',
    ],
    nuances: {
      default: {
        witnesses: 2,
        notarisation: 'Optional (self-proving affidavit)',
        holographic: 'Recognised in ~27 states',
        probate: 'State probate court (6–18 months)',
        spouseRights: 'Elective share / community property (varies by state)',
        minAge: 18,
        notes: 'Requirements vary by state. Louisiana follows Civil Law (forced heirship). Louisiana residents should consult a notary public who is also an attorney.',
      },
      Louisiana: {
        witnesses: 2,
        notarisation: 'Required (notarial will)',
        holographic: 'Recognised',
        probate: 'Succession proceedings',
        spouseRights: 'Community property + forced heirship for children under 24',
        minAge: 16,
        notes: 'Louisiana follows Civil Law. Forced heirship reserves a portion of the estate for children. A notarial testament must be executed before a notary and two witnesses.',
      },
    },
  },
  {
    value: 'CA', label: '🇨🇦 Canada', legalSystem: 'Common Law (Civil Law in Québec)',
    executorTerm: 'Estate Trustee / Liquidator (QC)',
    regions: [
      'Alberta','British Columbia','Manitoba','New Brunswick',
      'Newfoundland and Labrador','Northwest Territories','Nova Scotia','Nunavut',
      'Ontario','Prince Edward Island','Québec','Saskatchewan','Yukon',
    ],
    nuances: {
      default: {
        witnesses: 2,
        notarisation: 'Optional (notarial will is self-proving)',
        holographic: 'Recognised in most provinces',
        probate: 'Probate / Certificate of Appointment (varies by province)',
        spouseRights: 'Spousal election rights vary by province',
        minAge: 18,
        notes: 'Each province has its own Wills Act. A notarial will in Québec does not require probate. Ontario requires two witnesses who are not beneficiaries.',
      },
      Québec: {
        witnesses: 2,
        notarisation: 'Strongly recommended (notarial will avoids probate)',
        holographic: 'Recognised (must be entirely handwritten)',
        probate: 'Notarial wills bypass probate; others require court verification',
        spouseRights: 'Partnership of acquests (default matrimonial regime)',
        minAge: 18,
        notes: 'Québec follows Civil Law. A notarial will is executed before a notary and two witnesses and does not require probate. Holographic wills must be entirely in the testator\'s handwriting with no witnesses.',
      },
    },
  },
  {
    value: 'MX', label: '🇲🇽 Mexico', legalSystem: 'Civil Law',
    executorTerm: 'Albacea (Executor)',
    regions: [
      'Aguascalientes','Baja California','Baja California Sur','Campeche','Chiapas',
      'Chihuahua','Ciudad de México','Coahuila','Colima','Durango','Guanajuato',
      'Guerrero','Hidalgo','Jalisco','México','Michoacán','Morelos','Nayarit',
      'Nuevo León','Oaxaca','Puebla','Querétaro','Quintana Roo','San Luis Potosí',
      'Sinaloa','Sonora','Tabasco','Tamaulipas','Tlaxcala','Veracruz','Yucatán','Zacatecas',
    ],
    nuances: {
      default: {
        witnesses: 3,
        notarisation: 'Required (Notario Público)',
        holographic: 'Recognised but must be deposited with a notary',
        probate: 'Sucesión testamentaria (court or notary)',
        spouseRights: 'Community property (sociedad conyugal) or separation of assets',
        minAge: 16,
        notes: 'Wills in Mexico must be executed before a Notario Público (a public official, not a US-style notary). The most common form is the Testamento Público Abierto. Forced heirship applies to certain heirs.',
      },
    },
  },
  {
    value: 'BR', label: '🇧🇷 Brazil', legalSystem: 'Civil Law',
    executorTerm: 'Testamenteiro (Executor)',
    regions: [
      'Acre','Alagoas','Amapá','Amazonas','Bahia','Ceará','Distrito Federal',
      'Espírito Santo','Goiás','Maranhão','Mato Grosso','Mato Grosso do Sul',
      'Minas Gerais','Pará','Paraíba','Paraná','Pernambuco','Piauí',
      'Rio de Janeiro','Rio Grande do Norte','Rio Grande do Sul','Rondônia',
      'Roraima','Santa Catarina','São Paulo','Sergipe','Tocantins',
    ],
    nuances: {
      default: {
        witnesses: 5,
        notarisation: 'Required for public will (testamento público)',
        holographic: 'Recognised (testamento particular) — requires 3 witnesses at reading',
        probate: 'Inventário (court or extrajudicial notary)',
        spouseRights: 'Forced heirship: 50% reserved for necessary heirs (descendants, ascendants, spouse)',
        minAge: 16,
        notes: 'Brazilian law mandates forced heirship (legítima) — 50% of the estate must go to necessary heirs. Only the remaining 50% (quota disponível) can be freely distributed. A public will requires a notary and 5 witnesses.',
      },
    },
  },

  // ── Europe ────────────────────────────────────────────────────────────
  {
    value: 'GB', label: '🇬🇧 United Kingdom', legalSystem: 'Common Law (Scots Law in Scotland)',
    executorTerm: 'Executor / Executor-Dative (Scotland)',
    regions: ['England','Wales','Scotland','Northern Ireland'],
    nuances: {
      default: {
        witnesses: 2,
        notarisation: 'Not required',
        holographic: 'Not recognised in England & Wales; recognised in Scotland',
        probate: 'Grant of Probate (E&W) / Confirmation (Scotland)',
        spouseRights: 'Spouse entitled to intestacy share; no forced heirship in E&W',
        minAge: 18,
        notes: 'In England & Wales, the will must be signed by the testator in the presence of two independent witnesses who also sign. Witnesses and their spouses cannot be beneficiaries. Scotland has "prior rights" and "legal rights" (legitim) protecting spouses and children.',
      },
      Scotland: {
        witnesses: 1,
        notarisation: 'Not required',
        holographic: 'Recognised',
        probate: 'Confirmation granted by Sheriff Court',
        spouseRights: 'Legal rights (legitim): children entitled to one-third of moveable estate',
        minAge: 12,
        notes: 'Scottish law requires only one witness. Children have an automatic right (legitim) to one-third of moveable estate that cannot be overridden by a will. Prior rights protect a surviving spouse.',
      },
      'Northern Ireland': {
        witnesses: 2,
        notarisation: 'Not required',
        holographic: 'Not recognised',
        probate: 'Grant of Probate from Probate Office Belfast',
        spouseRights: 'Similar to England & Wales',
        minAge: 18,
        notes: 'Rules broadly similar to England & Wales. The Wills and Administration Proceedings (NI) Order 1994 governs wills in Northern Ireland.',
      },
    },
  },
  {
    value: 'DE', label: '🇩🇪 Germany', legalSystem: 'Civil Law',
    executorTerm: 'Testamentsvollstrecker (Executor)',
    regions: [
      'Baden-Württemberg','Bavaria','Berlin','Brandenburg','Bremen','Hamburg',
      'Hesse','Lower Saxony','Mecklenburg-Vorpommern','North Rhine-Westphalia',
      'Rhineland-Palatinate','Saarland','Saxony','Saxony-Anhalt',
      'Schleswig-Holstein','Thuringia',
    ],
    nuances: {
      default: {
        witnesses: 0,
        notarisation: 'Optional (notarial will is safer)',
        holographic: 'Fully recognised (entirely handwritten and signed)',
        probate: 'Erbschein (certificate of inheritance) from Nachlassgericht',
        spouseRights: 'Pflichteil (forced share): spouse and children entitled to 50% of statutory share',
        minAge: 18,
        notes: 'Germany recognises holographic wills — entirely handwritten, dated, and signed, with no witnesses needed. Forced heirship (Pflichteil) entitles close relatives to claim 50% of their statutory intestacy share even if excluded from the will. A notarial will is stored in the Central Wills Register.',
      },
    },
  },
  {
    value: 'FR', label: '🇫🇷 France', legalSystem: 'Civil Law',
    executorTerm: 'Exécuteur Testamentaire',
    regions: [
      'Auvergne-Rhône-Alpes','Bourgogne-Franche-Comté','Bretagne','Centre-Val de Loire',
      'Corse','Grand Est','Hauts-de-France','Île-de-France','Normandie',
      'Nouvelle-Aquitaine','Occitanie','Pays de la Loire',"Provence-Alpes-Côte d'Azur",
    ],
    nuances: {
      default: {
        witnesses: 2,
        notarisation: 'Required for authentic will (testament authentique)',
        holographic: 'Recognised (testament olographe — entirely handwritten)',
        probate: 'Acte de notoriété from a notaire',
        spouseRights: 'Réserve héréditaire: children have guaranteed inheritance share',
        minAge: 18,
        notes: 'French law has strict forced heirship (réserve héréditaire). One child = 50% reserved; two children = 66.7% reserved; three or more = 75% reserved. Only the "quotité disponible" can be freely distributed. EU Succession Regulation 650/2012 applies to cross-border estates.',
      },
    },
  },
  {
    value: 'ES', label: '🇪🇸 Spain', legalSystem: 'Civil Law (+ regional foral laws)',
    executorTerm: 'Albacea (Executor)',
    regions: [
      'Andalusia','Aragon','Asturias','Balearic Islands','Basque Country','Canary Islands',
      'Cantabria','Castile and León','Castile-La Mancha','Catalonia','Extremadura',
      'Galicia','La Rioja','Madrid','Murcia','Navarre','Valencia',
    ],
    nuances: {
      default: {
        witnesses: 3,
        notarisation: 'Required (before Notario)',
        holographic: 'Recognised but must be filed with court after death',
        probate: 'Declaración de herederos / Escritura de aceptación de herencia',
        spouseRights: 'Legítima: two-thirds of estate reserved for children and descendants',
        minAge: 14,
        notes: 'Spain has forced heirship (legítima). Two-thirds of the estate is reserved for descendants. Regional foral laws in Catalonia, Basque Country, Navarre, Aragon, Galicia, and Balearic Islands may differ significantly from the common civil code.',
      },
    },
  },
  {
    value: 'IT', label: '🇮🇹 Italy', legalSystem: 'Civil Law',
    executorTerm: 'Esecutore Testamentario',
    regions: [
      'Abruzzo','Basilicata','Calabria','Campania','Emilia-Romagna','Friuli-Venezia Giulia',
      'Lazio','Liguria','Lombardy','Marche','Molise','Piedmont','Puglia','Sardinia',
      'Sicily','Trentino-Alto Adige','Tuscany','Umbria','Valle d\'Aosta','Veneto',
    ],
    nuances: {
      default: {
        witnesses: 2,
        notarisation: 'Required for public will (testamento pubblico)',
        holographic: 'Recognised (testamento olografo — entirely handwritten)',
        probate: 'Dichiarazione di successione filed with Revenue Agency',
        spouseRights: 'Quota di riserva: spouse and children have guaranteed shares',
        minAge: 18,
        notes: 'Italian law enforces quota di riserva (forced heirship). Spouse alone = 50%; one child = 50%; spouse + one child = 25% each; multiple children = 66.7% total. A holographic will must be entirely handwritten, dated, and signed. EU Succession Regulation applies.',
      },
    },
  },
  {
    value: 'NL', label: '🇳🇱 Netherlands', legalSystem: 'Civil Law',
    executorTerm: 'Executeur',
    regions: [
      'Drenthe','Flevoland','Friesland','Gelderland','Groningen','Limburg',
      'North Brabant','North Holland','Overijssel','South Holland','Utrecht','Zeeland',
    ],
    nuances: {
      default: {
        witnesses: 2,
        notarisation: 'Required (before a civil-law notary)',
        holographic: 'Not recognised (must be notarial)',
        probate: 'Verklaring van erfrecht from a notary',
        spouseRights: 'Legitieme portie: children entitled to half their intestacy share',
        minAge: 16,
        notes: 'Dutch wills must be executed before a civil-law notary (notaris). There is no holographic will. Children have a legitimate portion (legitieme portie) equal to half their intestacy share, which can be claimed as a money claim against the estate.',
      },
    },
  },
  {
    value: 'SE', label: '🇸🇪 Sweden', legalSystem: 'Civil Law (Nordic)',
    executorTerm: 'Testamentsexekutor',
    regions: [
      'Blekinge','Dalarna','Gävleborg','Gotland','Halland','Jämtland','Jönköping',
      'Kalmar','Kronoberg','Norrbotten','Örebro','Östergötland','Skåne',
      'Södermanland','Stockholm','Uppsala','Värmland','Västerbotten',
      'Västernorrland','Västmanland','Västra Götaland',
    ],
    nuances: {
      default: {
        witnesses: 2,
        notarisation: 'Not required',
        holographic: 'Not recognised',
        probate: 'Bouppteckning (estate inventory) filed with Tax Agency',
        spouseRights: 'Laglott: children entitled to half their intestacy share',
        minAge: 18,
        notes: 'Swedish wills require two witnesses present simultaneously. Children have a right to laglott (half their intestacy share). A surviving spouse has a right to inherit before children in many cases. Estate inventory (bouppteckning) must be filed within 3 months.',
      },
    },
  },
  {
    value: 'CH', label: '🇨🇭 Switzerland', legalSystem: 'Civil Law',
    executorTerm: 'Willensvollstrecker / Exécuteur Testamentaire',
    regions: [
      'Aargau','Appenzell Ausserrhoden','Appenzell Innerrhoden','Basel-Landschaft',
      'Basel-Stadt','Bern','Fribourg','Geneva','Glarus','Graubünden','Jura','Lucerne',
      'Neuchâtel','Nidwalden','Obwalden','Schaffhausen','Schwyz','Solothurn',
      'St. Gallen','Thurgau','Ticino','Uri','Valais','Vaud','Zug','Zurich',
    ],
    nuances: {
      default: {
        witnesses: 2,
        notarisation: 'Required for public will; optional for holographic',
        holographic: 'Recognised (entirely handwritten, dated, signed)',
        probate: 'Cantonal probate authority',
        spouseRights: 'Pflichtteil: spouse and descendants have reserved shares',
        minAge: 18,
        notes: 'Switzerland updated its inheritance law in 2023, reducing forced shares. Descendants\' reserved share is now 50% of their intestacy share (down from 75%). Spouses/registered partners retain 50%. Holographic wills must be entirely handwritten. Rules vary slightly by canton.',
      },
    },
  },

  // ── Asia-Pacific ──────────────────────────────────────────────────────
  {
    value: 'AU', label: '🇦🇺 Australia', legalSystem: 'Common Law',
    executorTerm: 'Executor',
    regions: [
      'Australian Capital Territory','New South Wales','Northern Territory',
      'Queensland','South Australia','Tasmania','Victoria','Western Australia',
    ],
    nuances: {
      default: {
        witnesses: 2,
        notarisation: 'Not required',
        holographic: 'Not generally recognised (except SA, QLD with conditions)',
        probate: 'Probate from Supreme Court of each state/territory',
        spouseRights: 'No forced heirship; family provision claims available',
        minAge: 18,
        notes: 'Each Australian state and territory has its own Wills Act. Two adult independent witnesses required — they cannot be beneficiaries or their spouses. Family provision legislation allows eligible relatives to challenge the will in court. Enduring Powers of Attorney are separate documents.',
      },
    },
  },
  {
    value: 'NZ', label: '🇳🇿 New Zealand', legalSystem: 'Common Law',
    executorTerm: 'Executor',
    regions: ['Auckland','Bay of Plenty','Canterbury','Gisborne','Hawke\'s Bay',
      'Manawatū-Whanganui','Marlborough','Nelson','Northland','Otago',
      'Southland','Taranaki','Tasman','Waikato','Wellington','West Coast'],
    nuances: {
      default: {
        witnesses: 2,
        notarisation: 'Not required',
        holographic: 'Not recognised',
        probate: 'Grant of Probate from High Court',
        spouseRights: 'Property (Relationships) Act: relationship property split 50/50',
        minAge: 18,
        notes: 'New Zealand\'s Property (Relationships) Act 1976 means a surviving spouse/partner may claim 50% of relationship property regardless of the will. The Family Protection Act 1955 allows family members to make claims for adequate provision. Two independent witnesses required.',
      },
    },
  },
  {
    value: 'IN', label: '🇮🇳 India', legalSystem: 'Common Law (personal laws vary by religion)',
    executorTerm: 'Executor',
    regions: [
      'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa',
      'Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala',
      'Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland',
      'Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura',
      'Uttar Pradesh','Uttarakhand','West Bengal',
      'Andaman and Nicobar Islands','Chandigarh','Delhi','Jammu and Kashmir',
      'Ladakh','Lakshadweep','Puducherry',
    ],
    nuances: {
      default: {
        witnesses: 2,
        notarisation: 'Not required but recommended',
        holographic: 'Recognised under Indian Succession Act',
        probate: 'Probate from High Court (mandatory in some states)',
        spouseRights: 'Varies by religion (Hindu, Muslim, Christian, Parsi laws)',
        minAge: 18,
        notes: 'India\'s succession law is complex and religion-dependent. The Indian Succession Act 1925 applies to Christians, Parsis, and others. Hindu Succession Act applies to Hindus, Buddhists, Sikhs, and Jains. Muslim personal law (no will for more than 1/3 of estate without heirs\' consent). Probate is mandatory in West Bengal, Bihar, Odisha, and major cities. Stamp duty may apply.',
      },
    },
  },
  {
    value: 'SG', label: '🇸🇬 Singapore', legalSystem: 'Common Law',
    executorTerm: 'Executor',
    regions: ['Central Region','East Region','North Region','North-East Region','West Region'],
    nuances: {
      default: {
        witnesses: 2,
        notarisation: 'Not required',
        holographic: 'Not recognised',
        probate: 'Grant of Probate from Family Justice Courts',
        spouseRights: 'No forced heirship (except Muslim law)',
        minAge: 21,
        notes: 'Singapore follows the Wills Act (Cap 352). Minimum age is 21 (exception for married persons under 21). Two independent witnesses who are not beneficiaries or their spouses. Muslims are governed by the Administration of Muslim Law Act — only 1/3 of estate can be freely willed; 2/3 distributed per faraid (Islamic inheritance). CPF (pension) funds do not form part of the estate and must be separately nominated.',
      },
    },
  },
  {
    value: 'JP', label: '🇯🇵 Japan', legalSystem: 'Civil Law',
    executorTerm: '遺言執行者 (Will Executor)',
    regions: [
      'Aichi','Akita','Aomori','Chiba','Ehime','Fukui','Fukuoka','Fukushima',
      'Gifu','Gunma','Hiroshima','Hokkaido','Hyogo','Ibaraki','Ishikawa',
      'Iwate','Kagawa','Kagoshima','Kanagawa','Kochi','Kumamoto','Kyoto',
      'Mie','Miyagi','Miyazaki','Nagano','Nagasaki','Nara','Niigata','Oita',
      'Okayama','Okinawa','Osaka','Saga','Saitama','Shiga','Shimane','Shizuoka',
      'Tochigi','Tokushima','Tokyo','Tottori','Toyama','Wakayama','Yamagata',
      'Yamaguchi','Yamanashi',
    ],
    nuances: {
      default: {
        witnesses: 2,
        notarisation: 'Required for notarised will (公正証書遺言)',
        holographic: 'Recognised (自筆証書遺言 — entirely handwritten except property schedule)',
        probate: 'Family Court probate (検認) required for holographic/secret wills',
        spouseRights: 'Ihou-bun (遺留分): spouse and children have reserved shares',
        minAge: 15,
        notes: 'Japan has three will types: holographic (自筆証書), notarised (公正証書), and secret (秘密証書). Forced share (ihou-bun 遺留分) is 50% of the statutory share for spouse, children, and parents. Since 2019, holographic property schedules can be typed. Notarised wills are stored in the Japan Notary Association system and do not require probate.',
      },
    },
  },
  {
    value: 'CN', label: '🇨🇳 China', legalSystem: 'Civil Law',
    executorTerm: '遗嘱执行人 (Will Executor)',
    regions: [
      'Anhui','Beijing','Chongqing','Fujian','Gansu','Guangdong','Guangxi',
      'Guizhou','Hainan','Hebei','Heilongjiang','Henan','Hong Kong SAR',
      'Hubei','Hunan','Inner Mongolia','Jiangsu','Jiangxi','Jilin','Liaoning',
      'Macau SAR','Ningxia','Qinghai','Shaanxi','Shandong','Shanghai',
      'Shanxi','Sichuan','Tibet','Tianjin','Xinjiang','Yunnan','Zhejiang',
    ],
    nuances: {
      default: {
        witnesses: 2,
        notarisation: 'Strongly recommended (notarised wills had highest priority until 2021)',
        holographic: 'Recognised (entirely handwritten, dated, signed)',
        probate: 'No formal probate system; notary handles inheritance',
        spouseRights: 'Forced heirship: spouse, children, and parents are statutory heirs',
        minAge: 18,
        notes: 'China\'s Civil Code (2021) removed the prior priority of notarised wills — the most recent valid will now takes precedence. Five will types recognised: notarised, self-written, printed, recorded, and oral (emergency only). Spouse, children, and parents are first-order statutory heirs. Hong Kong and Macau SARs have separate legal systems.',
      },
      'Hong Kong SAR': {
        witnesses: 2,
        notarisation: 'Not required',
        holographic: 'Not recognised',
        probate: 'Grant of Probate / Letters of Administration from High Court',
        spouseRights: 'No forced heirship; family provision claims available',
        minAge: 18,
        notes: 'Hong Kong follows English common law under the Wills Ordinance (Cap 30). Two independent witnesses required. No forced heirship, but the Inheritance (Provision for Family and Dependants) Ordinance allows family members to make claims. MPF (pension) assets must be separately nominated.',
      },
    },
  },

  // ── Middle East & Africa ──────────────────────────────────────────────
  {
    value: 'AE', label: '🇦🇪 UAE', legalSystem: 'Civil Law + Sharia',
    executorTerm: 'Executor / Wasi',
    regions: [
      'Abu Dhabi','Ajman','Dubai','Fujairah','Ras Al Khaimah','Sharjah','Umm Al Quwain',
    ],
    nuances: {
      default: {
        witnesses: 2,
        notarisation: 'Required (DIFC or Abu Dhabi courts for expats)',
        holographic: 'Not recommended under Sharia',
        probate: 'Sharia court for Muslims; DIFC/ADGM courts for non-Muslims',
        spouseRights: 'Sharia inheritance rules apply to Muslims',
        minAge: 21,
        notes: 'Non-Muslim expats can register a will with the DIFC Wills Service Centre or Abu Dhabi Judicial Department to ensure their assets are distributed per their wishes rather than Sharia law. Without a registered will, Sharia law applies to all assets in the UAE regardless of nationality. Highly recommended for all expat residents.',
      },
    },
  },
  {
    value: 'ZA', label: '🇿🇦 South Africa', legalSystem: 'Mixed (Roman-Dutch + Common Law)',
    executorTerm: 'Executor',
    regions: [
      'Eastern Cape','Free State','Gauteng','KwaZulu-Natal','Limpopo',
      'Mpumalanga','North West','Northern Cape','Western Cape',
    ],
    nuances: {
      default: {
        witnesses: 2,
        notarisation: 'Not required',
        holographic: 'Not recognised',
        probate: 'Master of the High Court issues Letters of Executorship',
        spouseRights: 'Accrual system (default); community of property possible',
        minAge: 16,
        notes: 'South Africa\'s Wills Act 7 of 1953 requires two competent witnesses who are 14+ and not beneficiaries. The Master of the High Court must approve the appointment of an executor. No forced heirship under common law, but the Maintenance of Surviving Spouses Act and customary law may apply. Customary marriages and unions have specific inheritance implications.',
      },
    },
  },
  {
    value: 'NG', label: '🇳🇬 Nigeria', legalSystem: 'Common Law (+ customary/Islamic law)',
    executorTerm: 'Executor',
    regions: [
      'Abia','Adamawa','Akwa Ibom','Anambra','Bauchi','Bayelsa','Benue','Borno',
      'Cross River','Delta','Ebonyi','Edo','Ekiti','Enugu','FCT Abuja','Gombe',
      'Imo','Jigawa','Kaduna','Kano','Katsina','Kebbi','Kogi','Kwara','Lagos',
      'Nasarawa','Niger','Ogun','Ondo','Osun','Oyo','Plateau','Rivers',
      'Sokoto','Taraba','Yobe','Zamfara',
    ],
    nuances: {
      default: {
        witnesses: 2,
        notarisation: 'Not required but recommended',
        holographic: 'Recognised under some state laws',
        probate: 'Probate Registry of State High Court',
        spouseRights: 'Varies by state and customary law',
        minAge: 18,
        notes: 'Nigeria has three legal systems: English common law (applies to statutory marriages), customary law (varies by ethnic group), and Islamic personal law (northern states). A statutory will must be signed in the presence of two witnesses. In many southern states, customary law may override a will for land in the home community. Strongly advised to consult a Nigerian solicitor.',
      },
    },
  },
  {
    value: 'KE', label: '🇰🇪 Kenya', legalSystem: 'Common Law',
    executorTerm: 'Executor',
    regions: [
      'Baringo','Bomet','Bungoma','Busia','Elgeyo-Marakwet','Embu','Garissa',
      'Homa Bay','Isiolo','Kajiado','Kakamega','Kericho','Kiambu','Kilifi',
      'Kirinyaga','Kisii','Kisumu','Kitui','Kwale','Laikipia','Lamu',
      'Machakos','Makueni','Mandera','Marsabit','Meru','Migori','Mombasa',
      'Murang\'a','Nairobi','Nakuru','Nandi','Narok','Nyamira','Nyandarua',
      'Nyeri','Samburu','Siaya','Taita-Taveta','Tana River','Tharaka-Nithi',
      'Trans Nzoia','Turkana','Uasin Gishu','Vihiga','Wajir','West Pokot',
    ],
    nuances: {
      default: {
        witnesses: 2,
        notarisation: 'Not required',
        holographic: 'Not recognised',
        probate: 'Probate and Administration from High Court or Magistrate Court',
        spouseRights: 'Law of Succession Act: spouse and children are first-priority heirs',
        minAge: 18,
        notes: 'Kenya\'s Law of Succession Act (Cap 160) governs wills. Two witnesses required who are not beneficiaries. Customary law may apply to agricultural land and personal property in rural areas. Islamic law applies to Muslims for personal matters. Grants of representation are issued by the High Court or Magistrate\'s Court depending on estate value.',
      },
    },
  },
];

// ── Get jurisdiction nuance for a country + region ─────────────────────────
export function getJurisdictionNuance(countryValue, regionValue) {
  const country = COUNTRIES.find(c => c.value === countryValue);
  if (!country) return null;
  const nuance = country.nuances[regionValue] || country.nuances.default;
  return { ...nuance, country: country.label, legalSystem: country.legalSystem, executorTerm: country.executorTerm };
}

export const MARITAL_STATUS_OPTIONS = [
  { value: 'single', label: 'Single' },
  { value: 'married', label: 'Married' },
  { value: 'divorced', label: 'Divorced' },
  { value: 'widowed', label: 'Widowed' },
  { value: 'domestic_partnership', label: 'Domestic Partnership / Civil Union' },
  { value: 'separated', label: 'Separated' },
];